import * as XLSX from "xlsx";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../../backend/firebaseConfig";
import { RATE_FIELDS, normaliseRates } from "./rateFields";
import { getAdviserRatesRef } from "./ratesService";

const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];
const IDENTITY_COLUMNS = {
  emailaddress: "email",
  adviseremail: "email",
  adviser: "adviser",
  practice: "practice",
};

const normaliseHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normaliseEmail = (value) => String(value ?? "").trim().toLowerCase();

const RATE_COLUMNS = RATE_FIELDS.reduce(
  (columns, field) => ({ ...columns, [normaliseHeader(field.label)]: field.key }),
  {},
);

const findHeader = (rows) => {
  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 20); rowIndex += 1) {
    const columns = {};
    (rows[rowIndex] || []).forEach((value, columnIndex) => {
      const key = normaliseHeader(value);
      const field = IDENTITY_COLUMNS[key] || RATE_COLUMNS[key];
      if (field) columns[field] = columnIndex;
    });

    if (
      columns.email !== undefined &&
      RATE_FIELDS.every((field) => columns[field.key] !== undefined)
    ) {
      return { rowIndex, columns };
    }
  }

  throw new Error(
    "The spreadsheet must contain Email Address and every IFA rate column.",
  );
};

const parseRate = (value, field) => {
  if (value === "" || value === null || value === undefined) return { value: "" };
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) {
    return { error: `${field.label} must be a positive number or blank.` };
  }

  if (field.type === "percentage") {
    return { value: number > 1 ? number / 100 : number };
  }
  return { value: number };
};

const parseWorkbookRows = async (file) => {
  if (!/\.(xlsx|xls)$/i.test(String(file?.name || ""))) {
    throw new Error("Please choose an .xlsx or .xls file.");
  }
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", raw: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error("The workbook does not contain a sheet.");
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    defval: "",
    raw: true,
  });
  const header = findHeader(rows);

  return rows
    .slice(header.rowIndex + 1)
    .map((row, index) => {
      const rates = {};
      const errors = [];
      RATE_FIELDS.forEach((field) => {
        const parsed = parseRate(row?.[header.columns[field.key]], field);
        rates[field.key] = parsed.value;
        if (parsed.error) errors.push(parsed.error);
      });
      return {
        rowNumber: header.rowIndex + index + 2,
        email: normaliseEmail(row?.[header.columns.email]),
        adviser: String(row?.[header.columns.adviser] ?? "").trim(),
        practice: String(row?.[header.columns.practice] ?? "").trim(),
        rates,
        errors,
      };
    })
    .filter((row) => row.email || row.adviser || row.practice || RATE_FIELDS.some((field) => row.rates[field.key] !== ""));
};

const valuesEqual = (first, second) => {
  if ((first === "" || first == null) && (second === "" || second == null)) return true;
  const firstNumber = Number(first);
  const secondNumber = Number(second);
  return Number.isFinite(firstNumber) && Number.isFinite(secondNumber)
    ? Math.abs(firstNumber - secondNumber) < 0.000001
    : String(first) === String(second);
};

export const previewRatesImport = async (file) => {
  if (!file) throw new Error("Please choose an Excel file first.");
  const parsedRows = await parseWorkbookRows(file);
  if (!parsedRows.length) throw new Error("No adviser rate rows were found.");

  const adviserSnapshot = await getDocs(collection(db, ...ADVISERS_PATH));
  const advisersByEmail = new Map();
  adviserSnapshot.docs.forEach((adviserDocument) => {
    const adviser = adviserDocument.data() || {};
    [adviser.email, adviserDocument.id].forEach((emailValue) => {
      const email = normaliseEmail(emailValue);
      if (email) advisersByEmail.set(email, { adviserId: adviserDocument.id, storedEmail: adviser.email || adviserDocument.id });
    });
  });

  const emailCounts = new Map();
  parsedRows.forEach((row) => {
    if (row.email) emailCounts.set(row.email, (emailCounts.get(row.email) || 0) + 1);
  });

  const rows = await Promise.all(parsedRows.map(async (row) => {
    if (!row.email) return { ...row, status: "Invalid", reason: "Email Address is empty." };
    if (!/^\S+@\S+\.\S+$/.test(row.email)) return { ...row, status: "Invalid", reason: "Email format is invalid." };
    if (row.errors.length) return { ...row, status: "Invalid", reason: row.errors.join(" ") };
    if ((emailCounts.get(row.email) || 0) > 1) return { ...row, status: "Duplicate", reason: "Email appears more than once." };
    const match = advisersByEmail.get(row.email);
    if (!match) return { ...row, status: "Not found", reason: "No Firebase adviser matches this email." };

    const rateSnapshot = await getDoc(getAdviserRatesRef(match.adviserId));
    const currentRates = normaliseRates(rateSnapshot.exists() ? rateSnapshot.data() : {});
    const changes = RATE_FIELDS.filter((field) => !valuesEqual(currentRates[field.key], row.rates[field.key])).map((field) => ({
      fieldPath: field.key,
      label: field.label,
      previousValue: currentRates[field.key],
      newValue: row.rates[field.key],
    }));
    return {
      ...row,
      ...match,
      currentRates,
      changes,
      status: changes.length ? "Changed" : "No change",
      reason: changes.length ? `${changes.length} rate field${changes.length === 1 ? "" : "s"} differ.` : "Already matches Firebase.",
    };
  }));

  const changedRows = rows.filter((row) => row.status === "Changed");
  const issueRows = rows.filter((row) => !["Changed", "No change"].includes(row.status));
  return {
    fileName: file.name,
    totalRows: rows.length,
    rows,
    changedRows,
    noChangeCount: rows.filter((row) => row.status === "No change").length,
    invalidCount: issueRows.length,
    valid: issueRows.length === 0,
  };
};

export const commitRatesImport = async (preview) => {
  if (!preview?.valid) throw new Error("Resolve every invalid, duplicate or unmatched row before importing.");
  const rows = Array.isArray(preview.changedRows) ? preview.changedRows : [];
  if (!rows.length) throw new Error("There are no rate changes to import.");
  const updatedBy = auth.currentUser?.email || "";
  const updatedByName = auth.currentUser?.displayName || updatedBy || "Unknown user";

  for (let start = 0; start < rows.length; start += 200) {
    const batch = writeBatch(db);
    rows.slice(start, start + 200).forEach((row) => {
      const ratesRef = getAdviserRatesRef(row.adviserId);
      batch.set(ratesRef, { ...row.rates, updatedAt: serverTimestamp(), updatedBy, updatedByName }, { merge: true });
      batch.set(doc(collection(ratesRef, "Edit Logs")), {
        adviserId: row.adviserId,
        rates: row.rates,
        changes: row.changes.map(({ fieldPath, previousValue, newValue }) => ({ fieldPath, previousValue, newValue })),
        changeCount: row.changes.length,
        source: "excel-bulk-upload",
        sourceFileName: preview.fileName || "",
        updatedAt: serverTimestamp(),
        updatedBy,
        updatedByName,
        editedAt: serverTimestamp(),
        editedBy: updatedBy,
      });
    });
    await batch.commit();
  }
  return { written: rows.length, unchanged: preview.noChangeCount || 0 };
};
